// The API sends PK times as America/New_York wall-clock strings
// ("YYYY-MM-DD HH:mm:ss"). Parsing them with `new Date()` would shift them by
// the viewer's timezone, so these helpers only ever read the string's parts.

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

const ET_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/;

type EtParts = {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
};

export const parseEt = (value: string | null | undefined): EtParts | null => {
  if (!value) return null;
  const m = ET_PATTERN.exec(value);
  if (!m) return null;
  const parts = {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: Number(m[4] ?? 0),
    minute: Number(m[5] ?? 0),
    second: Number(m[6] ?? 0)
  };
  return parts.month >= 1 && parts.month <= 12 ? parts : null;
};

// "Sep 30, 2026"
export const formatEtDate = (value: string | null | undefined): string => {
  const p = parseEt(value);
  if (!p) return value ?? "-";
  return `${MONTHS[p.month - 1]} ${p.day}, ${p.year}`;
};

// "Sep 30, 2026 9:14 PM ET"
export const formatEtDateTime = (value: string | null | undefined): string => {
  const p = parseEt(value);
  if (!p) return value ?? "-";
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  const suffix = p.hour < 12 ? "AM" : "PM";
  return `${formatEtDate(value)} ${hour12}:${String(p.minute).padStart(
    2,
    "0"
  )} ${suffix} ET`;
};

// "Sep 3 - Sep 5, 2026", or just "Sep 3, 2026" when it's a single day.
export const formatEtDateRange = (
  start: string | null | undefined,
  end: string | null | undefined
): string => {
  const a = parseEt(start);
  const b = parseEt(end);
  if (!a || !b) return formatEtDate(start ?? end);
  if (a.year === b.year && a.month === b.month && a.day === b.day) {
    return formatEtDate(start);
  }
  if (a.year === b.year) {
    return `${MONTHS[a.month - 1]} ${a.day} - ${formatEtDate(end)}`;
  }
  return `${formatEtDate(start)} - ${formatEtDate(end)}`;
};

// "2026-09" -> "Sep 2026"
export const formatEtMonth = (month: string): string => {
  const p = parseEt(`${month}-01`);
  return p ? `${MONTHS[p.month - 1]} ${p.year}` : month;
};

// "Jan 2024" style for timeline spans.
export const formatEtMonthYear = (value: string | null | undefined): string => {
  const p = parseEt(value);
  return p ? `${MONTHS[p.month - 1]} ${p.year}` : value ?? "-";
};
